// The Low Power Mode fallback for Keep screen awake (see wakeLock.ts).
//
// iOS DENIES the Screen Wake Lock in Low Power Mode — user-found at 18%
// battery: "it plays for a bit, then dims, then goes black". But iOS never
// sleeps the screen during MEDIA playback, Low Power Mode included (a video
// keeps playing at 15% battery). So while Keep screen awake is ON and the
// lock has been DECLINED, a tiny silent looping video plays instead — the
// NoSleep.js technique, with its MIT-licensed sub-second assets vendored in
// assets/keepAwakeMedia.ts.
//
// Facts this module encodes, each from NoSleep's issue history:
// - play() outside a user gesture rejects on iOS, so attempts ride
//   pointerdown (like the wake lock's own retry), where the activation is
//   live; a rejection is a normal answer and the next tap retries.
// - The mp4 does not loop cleanly: near its end the time is SEEKED back
//   (NoSleep's timeupdate trick) so playback never "ends" — an ended video
//   stops inhibiting sleep. The webm is under a second and simply loops.
// - Both assets carry a silent AUDIO track deliberately — iOS stopped
//   letting audio-less video inhibit the idle timer around iOS 15.
// - The element stays DETACHED from the DOM: it exists for its media
//   session, not for rendering, and appending it would put an invisible
//   element over the page.
// - iOS pauses the video whenever the page hides. Wanted — no battery spend
//   while the phone is pocketed; the next tap resumes it.
//
// Activity is published on its own bus (never through the wake-lock status —
// two writers on one bus race), deferred a macrotask like the lock's, so a
// subscriber re-render can never land inside a tap's pointerdown→click
// window and swallow a controlled checkbox's toggle (the avoid-b9 lesson).

import { useEffect, useSyncExternalStore } from 'react';
import { KEEP_AWAKE_MP4, KEEP_AWAKE_WEBM } from '@/assets/keepAwakeMedia';
import type { WakeLockDocumentLike } from '@/components/wakeLock';

/** The slice of HTMLVideoElement the controller needs — structural, faked in tests. */
export type FallbackVideoLike = {
  paused: boolean;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: 'playing' | 'pause', listener: () => void): void;
  removeEventListener(type: 'playing' | 'pause', listener: () => void): void;
};

/** Build the real detached video element, NoSleep-style. */
export function createFallbackVideo(doc: Document): HTMLVideoElement {
  const video = doc.createElement('video');
  video.setAttribute('title', 'Keep screen awake');
  video.setAttribute('playsinline', '');
  for (const [type, src] of [
    ['webm', KEEP_AWAKE_WEBM],
    ['mp4', KEEP_AWAKE_MP4],
  ] as const) {
    const source = doc.createElement('source');
    source.src = src;
    source.type = `video/${type}`;
    video.appendChild(source);
  }
  video.addEventListener('loadedmetadata', () => {
    if (video.duration <= 1) {
      // The webm source: sub-second, loops cleanly.
      video.setAttribute('loop', '');
    } else {
      // The mp4 source: seek back near the end so playback never ends.
      video.addEventListener('timeupdate', () => {
        if (video.currentTime > 0.5) video.currentTime = Math.random();
      });
    }
  });
  return video;
}

export type KeepAwakeFallbackController = {
  /** Want the video holding the screen? Idempotent; playing is best-effort. */
  setEnabled(enabled: boolean): void;
  /** Pause and stop listening. The controller is dead after. */
  dispose(): void;
};

export function createKeepAwakeFallback(
  video: FallbackVideoLike,
  doc: WakeLockDocumentLike = document,
  onActive: (active: boolean) => void = () => undefined,
): KeepAwakeFallbackController {
  let enabled = false;

  const attempt = () => {
    if (!enabled || !video.paused || doc.visibilityState !== 'visible') return;
    // No gesture (or a codec refusal) rejects: the next tap retries.
    void video.play().catch(() => undefined);
  };

  const onPlaying = () => onActive(true);
  const onPause = () => onActive(false);
  video.addEventListener('playing', onPlaying);
  video.addEventListener('pause', onPause);

  const onVisibilityChange = () => {
    if (doc.visibilityState === 'visible') attempt();
  };
  doc.addEventListener('visibilitychange', onVisibilityChange);
  doc.addEventListener('pointerdown', attempt);

  return {
    setEnabled(next: boolean) {
      if (next === enabled) return;
      enabled = next;
      if (next) attempt();
      else video.pause();
    },
    dispose() {
      doc.removeEventListener('visibilitychange', onVisibilityChange);
      doc.removeEventListener('pointerdown', attempt);
      enabled = false;
      video.pause();
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
    },
  };
}

// --- live activity, readable anywhere (the Device row combines it with the
// --- wake-lock status to say what is actually holding the screen) ---

let fallbackActive = false;
const activeListeners = new Set<() => void>();

/** Exported for the hook and for panel tests; app code goes via the hook. */
export function publishKeepAwakeFallbackActive(active: boolean): void {
  if (active === fallbackActive) return;
  fallbackActive = active;
  activeListeners.forEach((listener) => listener());
}

/** Is the fallback video currently holding the screen? */
export function useKeepAwakeFallbackActive(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      activeListeners.add(onChange);
      return () => activeListeners.delete(onChange);
    },
    () => fallbackActive,
  );
}

/** Run the fallback while `enabled` — App passes keepAwake && lock declined. */
export function useKeepAwakeFallback(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const controller = createKeepAwakeFallback(createFallbackVideo(document), document, (active) => {
      // Deferred like the wake-lock status — see the module note.
      setTimeout(() => publishKeepAwakeFallbackActive(active), 0);
    });
    controller.setEnabled(true);
    return () => {
      controller.dispose();
      // The real 'pause' event fires on a media task the removed listener no
      // longer sees; publish the end of the hold explicitly.
      setTimeout(() => publishKeepAwakeFallbackActive(false), 0);
    };
  }, [enabled]);
}
